import React, { useState, useEffect } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Fingerprint, Smartphone, Trash2, Plus, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function PasskeyManager() {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    loadPasskeys();
  }, []);

  const loadPasskeys = async () => {
    try {
      const response = await fetch('/api/passkey/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      const data = await response.json();
      setPasskeys(data);
    } catch (err) {
      console.error('Error loading passkeys:', err);
    } finally {
      setLoading(false);
    }
  };

  const registerPasskey = async () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name');
      return;
    }

    setRegistering(true);
    try {
      // Check if browser supports WebAuthn
      if (!window.PublicKeyCredential) {
        toast.error('Your browser doesn\'t support passkeys');
        return;
      }

      // Get registration options from server
      const optionsResponse = await fetch('/api/passkey/register/options', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!optionsResponse.ok) {
        const errorData = await optionsResponse.json();
        console.error('Options error:', errorData);
        throw new Error(errorData.message || 'Failed to get registration options');
      }

      const options = await optionsResponse.json();
      console.log('Registration options received:', options);

      // Start registration (this will prompt user for biometric/PIN)
      const credential = await startRegistration(options);
      console.log('Registration complete, credential:', credential);

      // Send credential to server for verification
      const verifyResponse = await fetch('/api/passkey/register/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          credential,
          deviceName: deviceName.trim()
        })
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        console.error('Verify error:', errorData);
        throw new Error(errorData.message || 'Failed to verify passkey');
      }

      toast.success('🎉 Passkey registered successfully!');
      setShowAddForm(false);
      setDeviceName('');
      await loadPasskeys();
    } catch (err) {
      console.error('Passkey registration error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled');
      } else {
        toast.error('Failed to register passkey. Please try again.');
      }
    } finally {
      setRegistering(false);
    }
  };

  const deletePasskey = async (id) => {
    try {
      const response = await fetch(`/api/passkey/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete passkey');
      }

      toast.success('✓ Passkey deleted from server', {
        description: 'Remember to also remove it from your device settings',
        duration: 8000
      });
      await loadPasskeys();
    } catch (err) {
      console.error('Error deleting passkey:', err);
      toast.error('Failed to delete passkey');
    }
  };

  const confirmDeletePasskey = (passkey) => {
    openConfirm({
      title: 'Delete this passkey?',
      description: `${passkey.device_name || 'Passkey'} will be removed from our server. You must also delete it from your device or browser settings to fully revoke it.`,
      confirmLabel: 'Delete passkey',
      isDestructive: true,
      children: (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 space-y-2">
          <p className="font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Remove it on your device after deleting:
          </p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Windows: Settings → Accounts → Sign-in options → Passkeys</li>
            <li>Chrome: chrome://settings/passkeys</li>
            <li>Edge: edge://settings/passkeys</li>
          </ul>
        </div>
      ),
      onConfirm: () => deletePasskey(passkey.id)
    });
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'platform') {
      return <Fingerprint className="w-5 h-5" />;
    }
    return <Smartphone className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Fingerprint className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 text-lg mb-1">What are Passkeys?</h3>
              <p className="text-sm text-blue-700 mb-2">
                Passkeys are a modern, passwordless way to sign in using your device's biometrics (Face ID, Touch ID, or Windows Hello) or PIN. 
                They're more secure than passwords and much easier to use!
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge variant="outline" className="bg-white">🔐 More Secure</Badge>
                <Badge variant="outline" className="bg-white">⚡ Faster Login</Badge>
                <Badge variant="outline" className="bg-white">🚫 No Passwords</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Passkeys List */}
      <Card className="shadow-lg">
        <CardHeader className="border-b bg-slate-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Fingerprint className="w-5 h-5 text-emerald-600" />
                Your Passkeys
              </CardTitle>
              <CardDescription className="mt-1">
                {passkeys.length === 0 
                  ? 'No passkeys registered yet'
                  : `${passkeys.length} passkey${passkeys.length > 1 ? 's' : ''} registered`}
              </CardDescription>
            </div>
            {!showAddForm && (
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Passkey
              </Button>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {showAddForm ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-4"
            >
              <h4 className="font-semibold text-emerald-900 mb-4">Register a New Passkey</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Device Name (e.g., "My iPhone", "Work Laptop")
                  </label>
                  <Input
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="Enter a name for this device"
                    className="max-w-md"
                    disabled={registering}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={registerPasskey}
                    disabled={registering || !deviceName.trim()}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    {registering ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Registering...
                      </>
                    ) : (
                      <>
                        <Fingerprint className="w-4 h-4 mr-2" />
                        Create Passkey
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAddForm(false);
                      setDeviceName('');
                    }}
                    variant="outline"
                    disabled={registering}
                  >
                    Cancel
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  💡 You'll be prompted to use your device's biometric authentication or PIN
                </p>
              </div>
            </motion.div>
          ) : null}

          {passkeys.length === 0 && !showAddForm ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Fingerprint className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 mb-4">No passkeys registered yet</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                Add a passkey to enable fast, secure, passwordless sign-in using your device's biometrics
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {passkeys.map((passkey) => (
                <motion.div
                  key={passkey.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg hover:border-emerald-300 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      {getDeviceIcon(passkey.device_type)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{passkey.device_name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-xs text-slate-500">
                          Added {new Date(passkey.created_at).toLocaleDateString()}
                        </p>
                        {passkey.last_used_at && (
                          <p className="text-xs text-slate-500">
                            • Last used {new Date(passkey.last_used_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => confirmDeletePasskey(passkey)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
